import React, { useEffect, useState } from 'react'
import { useShortenedLabels } from 'src/Libs/downloads/hooks/use-shortened-titles'
import { DownloadsParent, DownloadsSearchResultParent } from 'src/Libs/downloads/models'
import { BreadcrumbsLayout, useBreadcrumbStyles } from '../../BreadcrumbsLayout'
import { getItemName, goToDirectParent } from '../../../utils/breadcrumb-utils'
import { SxProps } from '@mui/system'
import { useTheme } from '@mui/material'
import { useCategoryDisplayName } from 'src/Libs/downloads/hooks'

interface Props {
  parentCategories: (DownloadsParent | DownloadsSearchResultParent)[]
  onClickCategory: (id: number) => void
  onClickBackButton?: () => void
  sx?: SxProps
}

/**
 * Breadcrumbs component for moving download items.
 * Will shorten breadcrumb titles if they exceed available space, beginning with the first one
 */
const MoveCategoryBreadcrumbs: React.FC<Props> = ({ parentCategories, onClickCategory, onClickBackButton, sx }) => {
  const { classes } = useBreadcrumbStyles()
  const theme = useTheme()
  const [breadcrumbs, setBreadcrumbs] = useState<{ title: string; onClick: () => void; className?: string }[]>([])
  const getCategoryDisplayName = useCategoryDisplayName()

  const iconButtonRef = React.useRef<HTMLDivElement>(null)
  const wrapperRef = React.useRef<HTMLDivElement>(null)
  const dotRef = React.useRef<HTMLDivElement>(null)

  const shortenTitles = useShortenedLabels({ parentCategories, iconButtonRef, wrapperRef, dotRef, contentSpacer: theme.space(1) })

  useEffect(() => {
    const lastIdx = parentCategories.length - 1

    const newBreadcrumbs = parentCategories.map((item, idx) => ({
      title: idx === lastIdx ? getItemName(item.id, getCategoryDisplayName(item.nameLanguages)) : shortenTitles[idx],
      onClick: () => onClickCategory(item.id),
      className: parentCategories.length - 1 === idx ? classes.lastBreadcrumbItem : classes.breadcrumbItem,
    }))

    setBreadcrumbs(newBreadcrumbs)
  }, [shortenTitles, parentCategories]) // eslint-disable-line react-hooks/exhaustive-deps

  if (parentCategories.length === 0) {
    return null
  }

  return (
    <BreadcrumbsLayout
      iconButtonRef={iconButtonRef}
      wrapperRef={wrapperRef}
      lastItemVariant={'h5'}
      dotRef={dotRef}
      onGoBack={() =>
        goToDirectParent(
          parentCategories.map(item => ({ name: getCategoryDisplayName(item.nameLanguages), id: item.id })),
          onClickCategory,
          onClickBackButton
        )
      }
      breadcrumbs={breadcrumbs}
      sx={sx}
    />
  )
}

export default MoveCategoryBreadcrumbs
